import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { faqs, siteInfo } from "@/mocks/site";

const GROUPS = [
  {
    id: "content",
    title: "内容与信息",
    desc: "关于信息来源、核查方式与版本变化的说明。",
    items: [faqs[0], faqs[1], faqs[2], faqs[3]],
    openFirst: true,
  },
  {
    id: "update",
    title: "更新与互动",
    desc: "内容多久更新一次，以及如何向我们提供线索。",
    items: [faqs[4], faqs[5], faqs[8]],
    openFirst: false,
  },
  {
    id: "principle",
    title: "立场与边界",
    desc: "我们如何看待事件，以及处理隐私与争议的原则。",
    items: [faqs[6], faqs[7]],
    openFirst: false,
  },
  {
    id: "reading",
    title: "阅读建议",
    desc: "普通读者在围观事件时可以怎么判断。",
    items: [faqs[9]],
    openFirst: false,
  },
];

export default function FaqPage() {
  useDocumentMeta({
    title: "常见问题｜网红事件怎么看、信息从哪来 - 吃瓜复盘局",
    description:
      "吃瓜复盘局常见问题：网红事件一般多久平息、复盘信息来自哪里、为什么同一事件有多个版本、如何看待事件反转、内容多久更新，以及我们对隐私与立场的边界说明。",
    keywords: "网红事件常见问题,网红事件信息来源,网红事件反转,事件复盘方法",
    canonical: `${siteInfo.domain}/faq`,
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${siteInfo.domain}/` },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${siteInfo.domain}/faq` },
        ],
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "常见问题" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            常见问题
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
            把读者问得最多的问题集中在这里。如果你关心的内容不在其中，也欢迎直接发邮件给我们。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-8">
            {GROUPS.map((group) => (
              <section key={group.id} id={group.id} className="scroll-mt-24">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {group.title}
                </h2>
                <p className="mt-2 text-sm text-foreground-600">{group.desc}</p>
                <div className="mt-5">
                  <FaqAccordion items={group.items} defaultOpen={group.openFirst ? 0 : -1} />
                </div>
              </section>
            ))}
          </div>

          <aside className="lg:col-span-4">
            <div className="space-y-6 lg:sticky lg:top-24">
              <nav
                aria-label="问题分类"
                className="rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <h2 className="font-heading text-sm font-bold text-foreground-950">问题分类</h2>
                <ul className="mt-4 space-y-1">
                  {GROUPS.map((group) => (
                    <li key={group.id}>
                      <a
                        href={`#${group.id}`}
                        className="block rounded-md px-3 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-200 hover:text-foreground-900"
                      >
                        {group.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="rounded-lg bg-primary-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">还有其他问题？</h2>
                <p className="mt-2 text-xs leading-6 text-foreground-600">
                  可以直接发邮件到 {siteInfo.email}，或者订阅事件更新，第一时间收到新复盘。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  订阅更新
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}